import { useCallback, useEffect, useState } from "react";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import {
	useGetDynamicsListSyncTriggerQuery,
	useGetDynamicsOAuthStatusQuery,
	useUpdateDynamicsListSyncTriggerMutation,
} from "@/pages/Private/redux/dynamics/dynamics.api";

import { FeaturesCodes } from "../../../../Users/enum/codes";
import { MsdynamicsSyncTriggerWithInherit } from "../../../../Company/schema/dynamics";

interface UseDynamicsSyncTriggerOptions {
	listId: number;
}

/**
 * The list's Dynamics 365 **record sync** trigger. There is deliberately no opportunity
 * counterpart: `msdynamics_deal_creation_trigger` is keyed by `companyId` alone, so the
 * opportunity trigger is a company setting and lives in the Dynamics settings tab.
 */
export const useDynamicsSyncTrigger = ({ listId }: UseDynamicsSyncTriggerOptions) => {
	const isDynamicsEnabled = useIsFeatureActive(FeaturesCodes.MSDYNAMICS_CRM);
	const { data: oauthStatus } = useGetDynamicsOAuthStatusQuery(undefined, {
		skip: !isDynamicsEnabled,
	});
	const isConnected = oauthStatus?.connected ?? false;

	const { data: listSyncTrigger } = useGetDynamicsListSyncTriggerQuery(listId, {
		skip: !isDynamicsEnabled || !isConnected || !listId,
	});
	const [updateListSyncTrigger] = useUpdateDynamicsListSyncTriggerMutation();

	const [selectedDynamicsSyncTrigger, setSelectedDynamicsSyncTrigger] = useState<
		MsdynamicsSyncTriggerWithInherit | undefined
	>(undefined);

	useEffect(() => {
		if (listSyncTrigger) {
			setSelectedDynamicsSyncTrigger(listSyncTrigger.trigger);
		}
	}, [listSyncTrigger]);

	const hasSyncTriggerChanged = useCallback(
		() =>
			isDynamicsEnabled &&
			isConnected &&
			selectedDynamicsSyncTrigger !== undefined &&
			selectedDynamicsSyncTrigger !== listSyncTrigger?.trigger,
		[isDynamicsEnabled, isConnected, selectedDynamicsSyncTrigger, listSyncTrigger?.trigger]
	);

	const saveSyncTrigger = useCallback(async () => {
		if (hasSyncTriggerChanged() && selectedDynamicsSyncTrigger) {
			await updateListSyncTrigger({
				listId,
				data: { trigger: selectedDynamicsSyncTrigger },
			}).unwrap();
		}
	}, [hasSyncTriggerChanged, selectedDynamicsSyncTrigger, listId, updateListSyncTrigger]);

	return {
		isDynamicsEnabled,
		isConnected,

		listSyncTrigger,
		selectedDynamicsSyncTrigger,
		setSelectedDynamicsSyncTrigger,

		hasSyncTriggerChanged,
		saveSyncTrigger,
	};
};
